import Decimal from 'decimal.js';

import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  DeathSectionDistributionStatus,
  PrimaryClientDeathEvent,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/DispositiveProvisionsFormV2.types';
import { DispositiveProvisionDispositionKind } from '@/types/schema';

/**
 * Computes the distribution status for each primary-client death event based on
 * their respective recipient lists.
 */
export function getDeathSectionDistributionStatus(
  deathEvent: PrimaryClientDeathEvent,
  firstDeathRecipients: DispositiveProvisionsFormRecipient[] | null | undefined,
  secondDeathRecipients: DispositiveProvisionsFormRecipient[] | null | undefined
): DeathSectionDistributionStatus {
  // Filter out placeholder recipients that have not been fully specified yet
  const firstRecipients = Array.isArray(firstDeathRecipients)
    ? firstDeathRecipients.filter((r) => !isPlaceholderRecipient(r))
    : [];
  const secondRecipients = Array.isArray(secondDeathRecipients)
    ? secondDeathRecipients.filter((r) => !isPlaceholderRecipient(r))
    : [];

  const { isFullyDistributed: firstFully, exceeds100Percent: firstExceeds } =
    calculateResiduaryRecipients(firstRecipients);
  const { isFullyDistributed: secondFully, exceeds100Percent: secondExceeds } =
    calculateResiduaryRecipients(secondRecipients);

  const firstEmpty = firstRecipients.length === 0;
  const secondEmpty = secondRecipients.length === 0;

  // A distribution is considered "validly" fully distributed only when it
  // combines a FULLY_DISTRIBUTED result *and* does NOT exceed 100%.
  const firstValidFully = firstFully && !firstExceeds;
  const secondValidFully = secondFully && !secondExceeds;

  // Both FULLY_DISTRIBUTED and EXCEEDS_100_PERCENT mean "all funds have been used"
  const firstUsedAllFunds = firstValidFully || firstExceeds;
  const secondUsedAllFunds = secondValidFully || secondExceeds;

  // FIRST_DEATH STATUS
  if (deathEvent === PrimaryClientDeathEvent.FIRST_DEATH) {
    if (firstExceeds) {
      return DeathSectionDistributionStatus.EXCEEDS_100_PERCENT;
    } else if (firstValidFully) {
      return DeathSectionDistributionStatus.FULLY_DISTRIBUTED;
    } else if (firstEmpty) {
      // If the first death is empty, the assets remain
      return DeathSectionDistributionStatus.ASSETS_REMAIN;
    } else {
      // Otherwise, behavior depends on second death status
      return secondUsedAllFunds
        ? DeathSectionDistributionStatus.ASSETS_REMAIN
        : DeathSectionDistributionStatus.NEEDS_RESIDUARY;
    }
  }

  // SECOND_DEATH STATUS

  if (firstUsedAllFunds) {
    // If the first death event used all funds, no assets remain for the second death event
    return DeathSectionDistributionStatus.NO_ASSETS_REMAIN;
  }
  if (secondExceeds) {
    return DeathSectionDistributionStatus.EXCEEDS_100_PERCENT;
  }
  if (secondValidFully) {
    return DeathSectionDistributionStatus.FULLY_DISTRIBUTED;
  }
  if (secondEmpty) {
    // If the second death is empty, the assets remain
    return DeathSectionDistributionStatus.ASSETS_REMAIN;
  }

  // Otherwise, funds did not get used up by the current recipients, so we need a residuary
  return DeathSectionDistributionStatus.NEEDS_RESIDUARY;
}

/**
 * A recipient is considered a "placeholder" (and therefore ignored in any
 * distribution calculations) when *all* of the following are `null` (or, in the
 * case of `dispositionKind_Select`, `''`):
 *   • recipientId
 *   • dispositionPercentage
 *   • dispositionKind_Select (only when dispositionKind_ButtonGroup is "other")
 */
export function isPlaceholderRecipient(
  recipient: DispositiveProvisionsFormRecipient | undefined | null
): boolean {
  if (!recipient) {
    return true;
  }

  const {
    recipientId,
    dispositionPercentage,
    dispositionAmount,
    dispositionKind_Select,
    dispositionKind_ButtonGroup,
  } = recipient;

  // Asset DPs are placeholders when no assets have been selected
  if (dispositionKind_ButtonGroup === 'asset') {
    const hasAssets =
      (recipient.assetGroupIds?.length ?? 0) > 0 ||
      (recipient.assetStaticIds?.length ?? 0) > 0 ||
      (recipient.insurancePolicyIds?.length ?? 0) > 0 ||
      (recipient.ownershipStakeIds?.length ?? 0) > 0;
    return !recipientId && !hasAssets;
  }

  // dispositionKind_Select should only be considered when dispositionKind_ButtonGroup is "other"
  const hasValidDispositionKind =
    dispositionKind_ButtonGroup === 'other' && dispositionKind_Select;

  const hasValidPercentage =
    dispositionKind_ButtonGroup ===
      DispositiveProvisionDispositionKind.Percentage && dispositionPercentage;

  const hasValidAmount =
    dispositionKind_ButtonGroup ===
      DispositiveProvisionDispositionKind.Amount && dispositionAmount;

  return (
    !recipientId &&
    !hasValidDispositionKind &&
    !hasValidPercentage &&
    !hasValidAmount
  );
}

/**
 * Determine whether the provided recipients fully distribute the assets.
 *
 * A death-event's recipients are considered fully distributed when EITHER:
 *   1. At least one recipient uses the Anything Left Over disposition, OR
 *   2. All consecutive percentage groups sum to exactly 100%.
 *
 * For mixed disposition types, recipients are processed sequentially:
 * - Consecutive percentage recipients form groups that must sum to 100%
 * - Fixed amount recipients interrupt percentage groups, creating new remainder pools
 * - Each percentage group must be complete (sum to 100%) for full distribution
 *
 * @returns An object containing:
 *   - isFullyDistributed: boolean indicating if assets are fully distributed
 *   - residuaryIndex: index of the residuary recipient (null if none exists)
 *     The residuary is either the first "Anything Left Over" recipient or
 *     the last recipient in the first consecutive group that sums to 100%
 */
export function calculateResiduaryRecipients(
  recipients: DispositiveProvisionsFormRecipient[]
): {
  isFullyDistributed: boolean;
  /**
   * The index of the residuary recipient that is considered the cut-off for
   * further distributions.  This remains for backwards-compatibility and is
   * equal to the first element of `residuaryIndices` when the assets are fully
   * distributed, otherwise `null`.
   */
  residuaryIndex: number | null;
  /**
   * All recipient indices (in the original `recipients` array) that make up the
   * percentage group which completes the distribution to a full 100 percent.
   * For the "Anything Left Over" disposition this array will contain the
   * single index of that recipient.  When the assets are not fully distributed
   * the array will be empty.
   */
  residuaryIndices: number[];
  exceeds100Percent: boolean;
} {
  const validRecipients = recipients.filter((r) => !isPlaceholderRecipient(r));

  if (validRecipients.length === 0) {
    return {
      isFullyDistributed: false,
      residuaryIndex: null,
      residuaryIndices: [],
      exceeds100Percent: false,
    };
  }

  // Process recipients sequentially to find the first complete distribution
  let currentPercentageGroup: Decimal = new Decimal(0);
  let hasActivePercentageGroup = false;
  let currentGroupIndices: number[] = [];
  let firstCompleteGroupInfo: {
    residuaryIndex: number;
    residuaryIndices: number[];
    exceeds100Percent: boolean;
  } | null = null;

  // Track cumulative "Anything left over" formulaic percentages across
  // consecutive ALO recipients. Like percentage groups, the ALO group resets
  // when interrupted by a non-ALO recipient.
  let aloPercentageSum: Decimal = new Decimal(0);
  let aloIndices: number[] = [];

  try {
    for (let i = 0; i < validRecipients.length; i++) {
      const recipient = validRecipients[i];
      if (!recipient) continue;

      const originalIndex = recipients.findIndex((r) => r === recipient);
      if (originalIndex === -1) continue;

      // Asset DPs distribute specific assets, not a percentage of the entity.
      // Skip them entirely in the entity-level distribution calculation.
      if (recipient.dispositionKind_ButtonGroup === 'asset') {
        continue;
      }

      const isPercentage =
        recipient?.dispositionKind_ButtonGroup ===
          DispositiveProvisionDispositionKind.Percentage &&
        recipient?.dispositionPercentage;

      const isAnythingLeftOver =
        recipient.dispositionKind_ButtonGroup === 'other' &&
        recipient?.dispositionKind_Select ===
          DispositiveProvisionDispositionKind.AnythingLeftOver;

      // A percentage recipient with a dispositionLimit (max dollar cap) won't
      // fully deplete its share of the pool, so treat it like a fixed-amount
      // recipient for group-building purposes: it interrupts the current
      // percentage group rather than contributing to a 100% total.
      const hasLimit =
        isPercentage && recipient.dispositionLimit?.greaterThan(0);

      if (isPercentage && !hasLimit) {
        // A percentage recipient interrupts any active ALO group
        aloPercentageSum = new Decimal(0);
        aloIndices = [];

        if (!hasActivePercentageGroup) {
          // Starting a new percentage group
          currentPercentageGroup = new Decimal(0);
          currentGroupIndices = [];
        }

        currentPercentageGroup = currentPercentageGroup.plus(
          recipient.dispositionPercentage!
        );
        hasActivePercentageGroup = true;
        currentGroupIndices.push(originalIndex);

        // Track the first complete group, but continue to detect exceeding.
        if (
          currentPercentageGroup.greaterThanOrEqualTo(100) &&
          !firstCompleteGroupInfo
        ) {
          firstCompleteGroupInfo = {
            residuaryIndex:
              currentGroupIndices[currentGroupIndices.length - 1] ?? -1,
            residuaryIndices: [...currentGroupIndices],
            exceeds100Percent: currentPercentageGroup.greaterThan(100),
          };
        } else if (
          firstCompleteGroupInfo &&
          currentPercentageGroup.greaterThan(100)
        ) {
          // Update exceeding status if we continue adding to the same group
          firstCompleteGroupInfo.exceeds100Percent = true;
        }
      } else {
        // Any non-percentage recipient interrupts the percentage group
        // If we had a complete group, return it; otherwise reset for next group
        if (firstCompleteGroupInfo) {
          return {
            isFullyDistributed: true,
            residuaryIndex: firstCompleteGroupInfo.residuaryIndex,
            residuaryIndices: firstCompleteGroupInfo.residuaryIndices,
            exceeds100Percent: firstCompleteGroupInfo.exceeds100Percent,
          };
        }

        // "Anything Left Over" handling: an ALO recipient with no explicit
        // percentage takes all remaining assets and immediately completes
        // distribution. An ALO with a partial percentage only completes
        // distribution once consecutive ALO percentages sum to >= 100%.
        // Like percentage groups, a non-ALO recipient in between resets
        // the running ALO sum.
        if (isAnythingLeftOver) {
          const formulaicPct = recipient.dispositionFormulaicPercentage;
          if (formulaicPct == null) {
            // No explicit percentage = takes all remaining. Fully distributes.
            return {
              isFullyDistributed: true,
              residuaryIndex: originalIndex,
              residuaryIndices: [originalIndex],
              exceeds100Percent: false,
            };
          }
          aloPercentageSum = aloPercentageSum.plus(formulaicPct);
          aloIndices.push(originalIndex);

          if (aloPercentageSum.greaterThanOrEqualTo(100)) {
            return {
              isFullyDistributed: true,
              residuaryIndex: aloIndices[aloIndices.length - 1] ?? -1,
              residuaryIndices: [...aloIndices],
              exceeds100Percent: aloPercentageSum.greaterThan(100),
            };
          }
          // ALO percentages don't sum to 100% yet; fall through to reset
          // the percentage group like any other non-percentage recipient
        } else {
          // Any non-ALO, non-percentage recipient interrupts the ALO group
          aloPercentageSum = new Decimal(0);
          aloIndices = [];
        }

        // Reset percentage group for any other non-percentage recipient
        currentPercentageGroup = new Decimal(0);
        hasActivePercentageGroup = false;
        currentGroupIndices = [];
      }
    }

    // Return the first complete group if found
    if (firstCompleteGroupInfo) {
      return {
        isFullyDistributed: true,
        residuaryIndex: firstCompleteGroupInfo.residuaryIndex,
        residuaryIndices: firstCompleteGroupInfo.residuaryIndices,
        exceeds100Percent: firstCompleteGroupInfo.exceeds100Percent,
      };
    }

    // No complete distribution found
    return {
      isFullyDistributed: false,
      residuaryIndex: null,
      residuaryIndices: [],
      exceeds100Percent: false,
    };
  } catch {
    return {
      isFullyDistributed: false,
      residuaryIndex: null,
      residuaryIndices: [],
      exceeds100Percent: false,
    };
  }
}

/**
 * Determines which recipient indices need an asset ordering warning.
 *
 * Asset-specific DPs must be ordered ABOVE entity-level DPs (%, $, f(x)).
 * Any non-placeholder asset DP that appears after any non-placeholder
 * entity-level DP gets a warning.
 */
export function getAssetOrderingWarningIndices(
  recipients: DispositiveProvisionsFormRecipient[]
): Set<number> {
  const warningIndices = new Set<number>();
  let hasSeenEntityLevelDp = false;

  for (let i = 0; i < recipients.length; i++) {
    const r = recipients[i];
    if (!r || isPlaceholderRecipient(r)) continue;

    if (r.dispositionKind_ButtonGroup === 'asset') {
      if (hasSeenEntityLevelDp) {
        warningIndices.add(i);
      }
    } else {
      hasSeenEntityLevelDp = true;
    }
  }

  return warningIndices;
}
